import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Outlet, createRootRoute, createRoute, createRouter } from '@tanstack/react-router';
import { useEffect, useState, type ReactNode } from 'react';
import { AdminContext } from './auth/AdminContext';
import { authErrorMessage, useAuth } from './auth/AuthProvider';
import { AppShell } from './components/AppShell';
import { Wordmark } from './components/Clapper';
import { AppLink, useGo, useRouteParams } from './components/Nav';
import { Banner, Button, Spinner } from './components/ui';
import { identify, reset, track } from './lib/amplitude';
import { ApiError, errorMessage } from './lib/api';
import { fetchMe, qk } from './queries/admin';
import { AdminsPage } from './routes/AdminsPage';
import { InvitesPage } from './routes/InvitesPage';
import { LoginPage } from './routes/LoginPage';
import { OverviewPage } from './routes/OverviewPage';
import { RecordingPage } from './routes/RecordingPage';
import { RecordingsPage } from './routes/RecordingsPage';
import { RequestsPage } from './routes/RequestsPage';
import { TeamPage, TeamsPage } from './routes/TeamsPage';
import { UserPage, UsersPage } from './routes/UsersPage';

/**
 * Routes, defined in code. A handful of them, hand-written and readable top to
 * bottom; no generated route tree.
 */

function FullPage({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-full flex-col items-center justify-center gap-4 bg-slate-950 px-6 text-center">
      {children}
    </div>
  );
}

function Restoring() {
  return (
    <FullPage>
      <Spinner size={22} className="text-chalk-faint" />
    </FullPage>
  );
}

function NotFound() {
  return (
    <FullPage>
      <Wordmark />
      <h1 className="text-h2 text-chalk">There is nothing at this address</h1>
      <AppLink to="/">
        <Button variant="primary">Go to the overview</Button>
      </AppLink>
    </FullPage>
  );
}

/**
 * Signing out, wherever the button for it is: say so, forget Firebase, drop
 * the cache, then drop the analytics identity so the next person at this
 * browser is a new user.
 */
function useSignOutAction() {
  const { signOut } = useAuth();
  const queryClient = useQueryClient();
  return () => {
    track('Signed Out');
    void signOut().then(() => {
      queryClient.clear();
      reset();
    });
  };
}

/** Signed in, but the API says this account is not in the admins table. */
function NotAdmin() {
  const { user } = useAuth();
  const signOut = useSignOutAction();
  return (
    <FullPage>
      <Wordmark />
      <h1 className="text-h2 text-chalk">This account is not an admin</h1>
      <p className="max-w-sm text-body text-chalk-dim">
        You are signed in as <span className="text-chalk">{user?.email}</span>. Ask an existing admin to add
        that address from the Admins page, or sign in with a different account.
      </p>
      <Button onClick={signOut}>Sign out</Button>
    </FullPage>
  );
}

/** A password account that has not proven its address yet. */
function Unverified() {
  const { user, sendVerification, refresh } = useAuth();
  const signOut = useSignOutAction();
  const queryClient = useQueryClient();
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);

  const send = () => {
    setError(null);
    sendVerification()
      .then(() => setSent(true))
      .catch((cause: unknown) => setError(authErrorMessage(cause)));
  };

  const check = () => {
    setChecking(true);
    refresh()
      .then(() => queryClient.invalidateQueries({ queryKey: qk.me() }))
      .catch((cause: unknown) => setError(authErrorMessage(cause)))
      .finally(() => setChecking(false));
  };

  return (
    <FullPage>
      <Wordmark />
      <h1 className="text-h2 text-chalk">Verify your email first</h1>
      <p className="max-w-sm text-body text-chalk-dim">
        Admission here is by email address, so <span className="text-chalk">{user?.email}</span> has to be
        proven before it counts. Send yourself the link, click it, then come back.
      </p>
      {error ? <Banner tone="danger">{error}</Banner> : null}
      {sent ? <Banner tone="info">Sent. Check your inbox, then click the button below.</Banner> : null}
      <div className="flex flex-wrap justify-center gap-2">
        <Button variant="primary" onClick={send} disabled={sent}>
          {sent ? 'Verification email sent' : 'Send verification email'}
        </Button>
        <Button onClick={check} busy={checking}>
          I have verified it
        </Button>
        <Button variant="ghost" onClick={signOut}>
          Sign out
        </Button>
      </div>
    </FullPage>
  );
}

/**
 * Everything behind the panel. Two doors: Firebase says who is holding the
 * browser, then the API says whether that person is an admin. Only past both
 * does the shell render.
 */
function AdminGate({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const signOut = useSignOutAction();
  const go = useGo();

  useEffect(() => {
    if (!loading && !user) {
      const here = window.location.pathname + window.location.search;
      go(here === '/' ? '/login' : `/login?next=${encodeURIComponent(here)}`, { replace: true });
    }
  }, [go, loading, user]);

  const me = useQuery({ queryKey: qk.me(), queryFn: fetchMe, enabled: Boolean(user), staleTime: 5 * 60_000 });

  // The Slate admin id, the moment the API has admitted them. Identify is a
  // no-op once it has run for this id, and the gate re-mounts on every page.
  const admin = me.data;
  useEffect(() => {
    if (admin) identify({ userId: admin.id, email: admin.email, name: admin.name });
  }, [admin]);

  if (loading || !user || me.isPending) return <Restoring />;

  if (me.isError) {
    const error = me.error;
    if (error instanceof ApiError && error.code === 'not_admin') return <NotAdmin />;
    if (error instanceof ApiError && error.code === 'email_unverified') return <Unverified />;
    return (
      <FullPage>
        <Wordmark />
        <h1 className="text-h2 text-chalk">Could not check who you are</h1>
        <p className="max-w-sm text-body text-chalk-dim">{errorMessage(error)}</p>
        <div className="flex gap-2">
          <Button variant="primary" onClick={() => void me.refetch()}>
            Try again
          </Button>
          <Button variant="ghost" onClick={signOut}>
            Sign out
          </Button>
        </div>
      </FullPage>
    );
  }

  return (
    <AdminContext.Provider value={me.data}>
      <AppShell>{children}</AppShell>
    </AdminContext.Provider>
  );
}

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

function gated(path: string, Page: () => ReactNode) {
  return createRoute({
    getParentRoute: () => rootRoute,
    path,
    component: function Gated() {
      return (
        <AdminGate>
          <Page />
        </AdminGate>
      );
    },
  });
}

const overviewRoute = gated('/', OverviewPage);
const usersRoute = gated('/users', UsersPage);
const userRoute = gated('/users/$id', function UserRoute() {
  const { id } = useRouteParams();
  return id ? <UserPage id={id} /> : <NotFound />;
});
const teamsRoute = gated('/teams', TeamsPage);
const teamRoute = gated('/teams/$id', function TeamRoute() {
  const { id } = useRouteParams();
  return id ? <TeamPage id={id} /> : <NotFound />;
});
const recordingsRoute = gated('/recordings', RecordingsPage);
const recordingRoute = gated('/recordings/$id', function RecordingRoute() {
  const { id } = useRouteParams();
  return id ? <RecordingPage id={id} /> : <NotFound />;
});
const requestsRoute = gated('/requests', RequestsPage);
const invitesRoute = gated('/invites', InvitesPage);
const adminsRoute = gated('/admins', AdminsPage);

const routeTree = rootRoute.addChildren([
  loginRoute,
  overviewRoute,
  usersRoute,
  userRoute,
  teamsRoute,
  teamRoute,
  recordingsRoute,
  recordingRoute,
  requestsRoute,
  invitesRoute,
  adminsRoute,
]);

export const router = createRouter({
  routeTree,
  defaultNotFoundComponent: NotFound,
});
